/**
 * Lectura y escritura de participantes para la pestaña Work.
 *
 * Va por las rutas DERIVADAS DEL CATÁLOGO (§6), las mismas que usa el agente.
 * No hay una API aparte para la interfaz, así que lo que se ve aquí no puede
 * divergir de lo que ve el agente.
 *
 * Los tipos salen de `api-types.ts`, generado desde el OpenAPI del backend:
 * si allí cambia un campo, esto deja de compilar.
 *
 * La CONTRASEÑA no aparece en este archivo y no es un olvido. El backend la
 * acepta al crear o actualizar y no la devuelve en ninguna lectura; aquí no se
 * pide, no se manda y no se enseña. Si algún día alguien la añade a un
 * formulario "por completitud", esto es lo que hay que leer antes.
 */
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { request } from "../lib/api-client.ts";
import { API_BASE } from "../lib/api-routes.ts";
import type { operations } from "../lib/api-types.ts";
import { PARTICIPANTS_PAGE_SIZE } from "../lib/limits.ts";

import { fromPeriod } from "./calendar.ts";

type SearchResponse =
  operations["find_participants"]["responses"][200]["content"]["application/json"]["data"];

export type ParticipantSummary = SearchResponse["participants"][number];

type DetailResponse =
  operations["get_participant"]["responses"][200]["content"]["application/json"]["data"];

export type ParticipantDetail = DetailResponse;
export type EnrollmentView = DetailResponse["enrollments"][number];

type EnrollBody =
  operations["enroll_participant"]["requestBody"]["content"]["application/json"];

export type EnrollInput = EnrollBody;

/**
 * Los filtros del padrón.
 *
 * El periodo compuesto `AAAA-MM` se partió en año y mes, y el rol --que era un
 * enum cerrado escrito en el front-- es ahora el id de un cargo que sale de su
 * listado. Aquí ya no hay ningún valor de negocio escrito a mano.
 */
export interface ParticipantFilters {
  readonly text?: string;
  readonly year?: number;
  readonly month?: number;
  readonly positionId?: string;
  readonly active?: boolean;
  /** `false`: sin vinculación en el mes (o en ninguno si no hay mes). */
  readonly linked?: boolean;
}

/**
 * Radix reserva la cadena vacía para "sin selección" y no la deja usar como
 * valor de una opción, así que "todos" se dice con una palabra.
 */
export const ALL = "all";

/**
 * Lo que hay puesto en los controles del filtro, tal cual se teclea.
 *
 * El MES no está aquí: lo manda la cabecera del panel, que es un solo mando
 * para todas las secciones. Con dos --uno arriba y otro entre estos filtros--
 * acaban diciendo meses distintos y nadie sabe cuál manda la tabla.
 */
export interface FilterControls {
  readonly text: string;
  readonly positionId: string;
  readonly active: string;
}

export const EMPTY_FILTERS: FilterControls = {
  text: "",
  positionId: ALL,
  active: ALL,
};

/**
 * De lo que hay en pantalla a lo que entiende el backend.
 *
 * El mes del panel entra como `AAAA-MM` y sale partido en año y mes, que es lo
 * que pide `find_participants` desde que la vinculación dejó de llevar el
 * periodo compuesto. La traducción ocurre aquí, en la frontera, y no en la
 * cabecera: el panel sigue hablando de meses como el resto del sistema.
 *
 * Si arriba hay un año entero, sale el año sin mes y el padrón enseña los doce
 * meses. Esa consulta solo la puede hacer esta sección, y por eso el año se
 * parte aquí en vez de exigirle un mes a todo el mundo.
 *
 * Cada campo se omite cuando no filtra, en vez de mandarse vacío: el contrato
 * distingue "sin filtro de estado" de "solo los inactivos", y un `active=`
 * vacío sería una tercera cosa que nadie ha definido.
 */
export const toFilters = (
  controls: FilterControls,
  period: string,
  unlinked = false,
): ParticipantFilters => {
  const text = controls.text.trim();
  const when = fromPeriod(period);

  return {
    ...(text === "" ? {} : { text }),
    ...(when ?? {}),
    ...(controls.positionId === ALL ? {} : { positionId: controls.positionId }),
    ...(controls.active === ALL
      ? {}
      : { active: controls.active === "active" }),
    ...(unlinked ? { linked: false } : {}),
  };
};

/** La consulta, ya como cadena. Se prueba aquí porque es el contrato de hecho. */
export const searchParams = (
  filters: ParticipantFilters,
  limit: number,
  offset = 0,
): URLSearchParams => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (offset > 0) params.set("offset", String(offset));
  if (filters.text !== undefined) params.set("text", filters.text);
  if (filters.year !== undefined) params.set("year", String(filters.year));
  if (filters.month !== undefined) params.set("month", String(filters.month));
  if (filters.positionId !== undefined) {
    params.set("positionId", filters.positionId);
  }
  if (filters.active !== undefined) {
    // OJO (2026-09-22): el backend publica `active` como booleano de consulta
    // pero su validación no lo coacciona desde el texto de la URL, así que hoy
    // responde 422 a `active=true`. Se manda lo que dice el contrato; el
    // arreglo es suyo. Cuando coaccione, esto ya funciona.
    params.set("active", String(filters.active));
  }
  // Mismo trato que `active`: se manda el booleano que publica el contrato.
  if (filters.linked !== undefined)
    params.set("linked", String(filters.linked));
  return params;
};

export const findParticipants = (
  filters: ParticipantFilters,
  limit = PARTICIPANTS_PAGE_SIZE,
  offset = 0,
): Promise<SearchResponse> =>
  request<SearchResponse>(
    `${API_BASE}/queries/find_participants?${searchParams(filters, limit, offset).toString()}`,
  );

export const getParticipant = (id: string): Promise<DetailResponse> =>
  request<DetailResponse>(
    `${API_BASE}/queries/get_participant?participantId=${encodeURIComponent(id)}`,
  );

/** Lo que el modelo guarda como null es "empleado directo", no un hueco. */
export const agencyLabel = (agency: string | null): string =>
  agency ?? "Directo";

/**
 * Activo o no, en la palabra que se enseña.
 *
 * Se lee como palabra y no como casilla marcada: en una tabla, una casilla
 * invita a pulsarla, y esto es una columna que se mira. Cambiarlo se cambia
 * desde la ficha.
 */
export const activeLabel = (active: boolean): string =>
  active ? "Activo" : "Inactivo";

/**
 * La búsqueda del padrón, por páginas (1.3).
 *
 * El padrón pasa del tope de una página --480 personas contra 100 por
 * página--, así que se pide de a trozos siguiendo el `nextOffset` que
 * devuelve el backend. Antes se pedía una sola página y las 380 de detrás no
 * se podían ver desde aquí.
 *
 * Los filtros entran en la clave, así que volver a un filtro ya usado sale de
 * la caché y no vuelve a preguntar. `placeholderData` deja en pantalla el
 * resultado anterior mientras llega el nuevo: sin eso, cada tecla vaciaba la
 * tabla y el salto era peor que la espera.
 */
export const PARTICIPANTS_KEY = ["participants"] as const;

export const participantsQuery = (
  filters: ParticipantFilters,
  limit = PARTICIPANTS_PAGE_SIZE,
) =>
  infiniteQueryOptions({
    queryKey: [...PARTICIPANTS_KEY, filters, limit] as const,
    queryFn: ({ pageParam }) => findParticipants(filters, limit, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last: SearchResponse) => last.nextOffset ?? undefined,
    placeholderData: keepPreviousData,
  });

export const participantQuery = (participantId: string) =>
  queryOptions({
    queryKey: ["participant", participantId] as const,
    queryFn: () => getParticipant(participantId),
  });

/**
 * Dar de baja o volver a dar de alta.
 *
 * No toca las vinculaciones --lo que esa persona hizo en marzo sigue siendo
 * cierto-- y tampoco la esconde de las búsquedas: para eso está el filtro de
 * estado. Es un dato de la persona, no un borrado disimulado.
 *
 * La ficha cambia ANTES de que el servidor conteste, porque el interruptor se
 * mira mientras se pulsa y medio segundo de nada se nota; si falla, vuelve
 * donde estaba y el error se enseña al lado.
 */
export const useSetParticipantActive = (participantId: string) => {
  const queryClient = useQueryClient();
  const key = ["participant", participantId] as const;

  return useMutation({
    mutationFn: (active: boolean) =>
      request(`${API_BASE}/commands/update_participant`, {
        method: "POST",
        body: JSON.stringify({ participantId, active }),
      }),

    onMutate: async (active) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ParticipantDetail>(key);
      if (previous !== undefined) {
        queryClient.setQueryData<ParticipantDetail>(key, {
          ...previous,
          active,
        });
      }
      return { previous };
    },

    onError: (_error, _active, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(key, context.previous);
      }
    },

    // La última palabra la tiene el servidor, y el listado también lo enseña.
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: key });
      await queryClient.invalidateQueries({ queryKey: PARTICIPANTS_KEY });
    },
  });
};

/**
 * Vincular a alguien con un mes.
 *
 * Es la MISMA herramienta que usa el agente (`enroll_participant`), con lo que
 * eso implica: el cargo y la región son ids de registros que ya existen, y
 * corregir una vinculación que ya está exige decirlo (`replace`), para que
 * pisar lo cargado nunca sea un descuido.
 */
export const useEnrollParticipant = (participantId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: EnrollInput) =>
      request(`${API_BASE}/commands/enroll_participant`, {
        method: "POST",
        body: JSON.stringify(input),
      }),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["participant", participantId],
      });
      await queryClient.invalidateQueries({ queryKey: PARTICIPANTS_KEY });
    },
  });
};

/**
 * El participante como registro de la telemetría (#5): quién es, cómo se le
 * localiza y dónde está vinculado ahora --la vinculación más reciente--.
 */
export const participantRecord = (participant: ParticipantDetail) => {
  const latest = [...participant.enrollments].sort(
    (a, b) => b.year - a.year || b.month - a.month,
  )[0];
  return {
    recordId: participant.id,
    code: participant.identifier,
    name: participant.fullName,
    email: participant.email,
    phone: participant.phone,
    position: latest?.position.name ?? null,
    region: latest?.region?.name ?? null,
  };
};
